import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { SETTINGS_KEYS } from '../../config/environment'
import { PermissionAction, PermissionType, permissionsService } from '../../services/permissions.service'
import { savedViewService, sortSavedViewsByStoredOrder } from '../../services/saved-view.service'
import { settingsService } from '../../services/settings.service'
import { toastService } from '../../services/toast.service'
import { useAppStore } from '../../store/useAppStore'
import { DisplayField, SavedView } from '../../types'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { DragDropSelect } from '../common/DragDropSelect'
import { PageHeader } from '../common/PageHeader'
import { PermissionsDialog, PermissionsFormData } from '../common/PermissionsDialog'
import './SavedViews.scss'

interface SavedViewsProps {
  className?: string
}

interface SortableSavedViewCardProps {
  view: SavedView
  isExpanded: boolean
  isDraggable: boolean
  children: (dragHandle: React.ReactNode) => React.ReactNode
}

const SortableSavedViewCard: React.FC<SortableSavedViewCardProps> = ({ view, isExpanded, isDraggable, children }) => {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: view.id,
    disabled: !isDraggable,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const dragHandle = isDraggable ? (
    <button
      type="button"
      className="saved-view-drag-handle"
      aria-label={t('drag_to_reorder')}
      title={t('drag_to_reorder')}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      {...attributes}
      {...listeners}
    >
      <i className="bi bi-grip-vertical"></i>
    </button>
  ) : null

  return (
    <div ref={setNodeRef} style={style} className={`saved-view-sortable ${isDragging ? 'is-dragging' : ''} ${isExpanded ? 'is-expanded' : ''}`}>
      {children(dragHandle)}
    </div>
  )
}

export const SavedViews: React.FC<SavedViewsProps> = ({ className }) => {
  const { t } = useTranslation()
  const sidebarSavedViews = useAppStore((state) => state.savedViews)
  const setSidebarSavedViews = useAppStore((state) => state.setSavedViews)
  const [savedViews, setSavedViews] = useState<SavedView[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [displayFields, setDisplayFields] = useState<DisplayField[]>([])
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null)
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null)
  const [expandedViews, setExpandedViews] = useState<Set<number>>(new Set())
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [selectedViewForPermissions, setSelectedViewForPermissions] = useState<SavedView | null>(null)

  const loadSavedViews = async () => {
    setLoading(true)
    setError(null)
    try {
      const views = await savedViewService.getAllSavedViews()

      // First set display fields
      const fields = savedViewService.getDisplayFields(t)
      setDisplayFields(fields)

      const transformedViews = (Array.isArray(views) ? views : []).map((view) => ({
        ...view,
        display_fields: Array.isArray(view.display_fields)
          ? view.display_fields.map((fieldId: any) =>
              typeof fieldId === 'string' ? fields.find((field) => field.id === fieldId) || { id: fieldId, name: fieldId } : fieldId
            )
          : [],
      }))

      const storedOrder: number[] = settingsService.get(SETTINGS_KEYS.SIDEBAR_VIEWS_SORT_ORDER) || []
      setSavedViews(sortSavedViewsByStoredOrder(transformedViews, storedOrder))
    } catch (err) {
      setError(t('error_loading_saved_views'))
    } finally {
      setLoading(false)
    }
  }

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }
    setSavedViews((prev) => {
      const oldIndex = prev.findIndex((v) => v.id === active.id)
      const newIndex = prev.findIndex((v) => v.id === over.id)
      if (oldIndex < 0 || newIndex < 0) {
        return prev
      }
      const reordered = arrayMove(prev, oldIndex, newIndex)
      const reorderedIds = reordered.map((v) => v.id)
      setSidebarSavedViews(sortSavedViewsByStoredOrder(sidebarSavedViews, reorderedIds))
      settingsService.updateSidebarViewsSort(reorderedIds).catch(() => {
        toastService.showError(t('error_saving_settings') || 'Error saving settings')
      })
      return reordered
    })
  }

  useEffect(() => {
    loadSavedViews()
  }, [])

  // Reload display fields when language changes
  useEffect(() => {
    setDisplayFields(savedViewService.getDisplayFields(t))
  }, [t])

  const handleInputChange = (viewId: number, field: keyof SavedView, value: any) => {
    setSavedViews((prev) => prev.map((view) => (view.id === viewId ? { ...view, [field]: value } : view)))
    setIsDirty(true)
  }

  const handleDisplayFieldsChange = (viewId: number, fields: DisplayField[]) => {
    setSavedViews((prev) => prev.map((view) => (view.id === viewId ? { ...view, display_fields: fields } : view)))
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!isDirty) return

    setSaving(true)
    setError(null)
    try {
      // Transform display_fields from objects to strings for backend
      const viewsToSave = savedViews.map((view) => ({
        ...view,
        display_fields:
          Array.isArray(view.display_fields) && view.display_fields.length > 0
            ? view.display_fields[0] && typeof view.display_fields[0] === 'object'
              ? (view.display_fields as DisplayField[]).map((field) => field.id)
              : (view.display_fields as string[])
            : undefined,
      }))

      await savedViewService.updateManySavedViews(viewsToSave)
      setIsDirty(false)
      // Show success message
    } catch (err) {
      setError(t('error_saving_saved_views'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (view: SavedView) => {
    const executeDelete = async () => {
      try {
        await savedViewService.deleteSavedView(view.id)
        setSavedViews((prev) => prev.filter((v) => v.id !== view.id))
        setShowConfirmDialog(false)
      } catch (err) {
        setError(t('error_deleting_saved_view'))
      }
    }

    setViewToDelete(view)
    setConfirmAction(() => executeDelete)
    setShowConfirmDialog(true)
  }

  const handleReset = () => {
    loadSavedViews()
    setIsDirty(false)
  }

  const getDisplayModeOptions = () => {
    return savedViewService.getDisplayModes(t)
  }

  const toggleViewExpanded = (viewId: number) => {
    setExpandedViews((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(viewId)) {
        newSet.delete(viewId)
      } else {
        newSet.add(viewId)
      }
      return newSet
    })
  }

  const isViewExpanded = (viewId: number) => {
    return expandedViews.has(viewId)
  }

  const filteredViews = useMemo(() => {
    if (!searchQuery.trim()) {
      return savedViews
    }
    const query = searchQuery.toLowerCase()
    return savedViews.filter((view) => view.name?.toLowerCase().includes(query))
  }, [savedViews, searchQuery])

  return (
    <div className={`saved-views ${className || ''}`}>
      <PageHeader title={t('saved_views')} info={t('saved_views_info')} infoLink="saved_views" />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="saved-views-search">
        <Form.Group>
          <Form.Control type="text" placeholder={t('search') + '...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </Form.Group>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault()
          handleSave()
        }}
      >
        {loading && (
          <div className="d-flex align-items-center justify-content-center py-5">
            <Spinner size="sm" className="me-2" />
            <span>{t('loading')}...</span>
          </div>
        )}

        {!loading && filteredViews.length === 0 && searchQuery.trim() && <div className="text-center text-muted py-5">{t('no_results')}</div>}

        {!loading && filteredViews.length === 0 && !searchQuery.trim() && (
          <div className="text-center text-muted py-5">{t('no_saved_views_defined')}</div>
        )}

        {!loading && filteredViews.length > 0 && (
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredViews.map((v) => v.id)} strategy={verticalListSortingStrategy}>
              <div className="saved-views-grid mb-3">
                {filteredViews.map((view) => {
                  const isExpanded = isViewExpanded(view.id)
                  const canReorder = !searchQuery.trim()
                  return (
                    <SortableSavedViewCard key={view.id} view={view} isExpanded={isExpanded} isDraggable={canReorder}>
                      {(dragHandle) => (
                        <Card className="saved-view-card">
                          <Card.Header className="saved-view-card-header" onClick={() => toggleViewExpanded(view.id)}>
                            <div className="d-flex align-items-center justify-content-between w-100">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2">
                                  {dragHandle}
                                  <div className="saved-view-card-title">{view.name || t('untitled')}</div>
                          {view.owner && !permissionsService.currentUserOwnsObject(view) && (
                            <Badge bg="info" className="saved-view-shared-badge">
                              <i className="bi bi-people-fill me-1"></i>
                              {t('shared')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {permissionsService.currentUserCan(PermissionAction.Change, PermissionType.SavedView) &&
                          permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, view) && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedViewForPermissions(view)
                                setShowPermissionsDialog(true)
                              }}
                              className="saved-view-permissions-btn"
                              title={t('permissions')}
                            >
                              <i className="bi bi-shield-lock"></i>
                            </Button>
                          )}
                        {permissionsService.currentUserCan(PermissionAction.Delete, PermissionType.SavedView) &&
                          permissionsService.currentUserOwnsObject(view) && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDelete(view)
                              }}
                              className="saved-view-delete-btn"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          )}
                        <Button
                          variant="link"
                          size="sm"
                          className="saved-view-expand-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleViewExpanded(view.id)
                          }}
                        >
                          <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  {isExpanded && (
                    <Card.Body>
                      {(() => {
                        const canChange = permissionsService.currentUserHasObjectPermissions(PermissionAction.Change, view)
                        return (
                          <>
                            <div className="row">
                              <div className="col-md-6">
                                <Form.Group className="mb-3">
                                  <Form.Label>{t('name')}</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={view.name || ''}
                                    onChange={(e) => handleInputChange(view.id, 'name', e.target.value)}
                                    disabled={!canChange}
                                  />
                                </Form.Group>
                              </div>
                              <div className="col-md-6">
                                <Form.Group className="mb-3">
                                  <Form.Label>{t('document_page_size')}</Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={view.page_size ?? ''}
                                    onChange={(e) => {
                                      const raw = e.target.value
                                      handleInputChange(view.id, 'page_size', raw === '' ? null : parseInt(raw, 10))
                                    }}
                                    disabled={!canChange}
                                  />
                                </Form.Group>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6">
                                <Form.Group className="mb-3">
                                  <Form.Label>{t('display_as')}</Form.Label>
                                  <Form.Select
                                    value={view.display_mode || 'table'}
                                    onChange={(e) => handleInputChange(view.id, 'display_mode', e.target.value)}
                                    disabled={!canChange}
                                  >
                                    {getDisplayModeOptions().map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                              </div>
                              <div className="col-md-6">
                                <div className="form-check form-switch mt-4">
                                  <Form.Check
                                    type="switch"
                                    id={`show_on_dashboard_${view.id}`}
                                    label={t('show_on_dashboard')}
                                    checked={view.show_on_dashboard}
                                    onChange={(e) => handleInputChange(view.id, 'show_on_dashboard', e.target.checked)}
                                    disabled={!canChange}
                                  />
                                </div>
                                <div className="form-check form-switch">
                                  <Form.Check
                                    type="switch"
                                    id={`show_in_sidebar_${view.id}`}
                                    label={t('show_in_sidebar')}
                                    checked={view.show_in_sidebar}
                                    onChange={(e) => handleInputChange(view.id, 'show_in_sidebar', e.target.checked)}
                                    disabled={!canChange}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12">
                                <Form.Group className="mb-3">
                                  <DragDropSelect
                                    title={t('show')}
                                    items={displayFields}
                                    value={
                                      Array.isArray(view.display_fields) && view.display_fields.length > 0
                                        ? view.display_fields[0] && typeof view.display_fields[0] === 'object'
                                          ? (view.display_fields as DisplayField[]).map((f) => f.id)
                                          : (view.display_fields as string[])
                                        : []
                                    }
                                    onChange={(selectedIds) => {
                                      const selectedFields = displayFields.filter((f) => selectedIds.includes(f.id))
                                      handleDisplayFieldsChange(view.id, selectedFields)
                                    }}
                                    emptyText={t('default')}
                                    disabled={!canChange}
                                  />
                                </Form.Group>
                              </div>
                            </div>
                          </>
                        )
                      })()}
                    </Card.Body>
                  )}
                </Card>
              )}
                    </SortableSavedViewCard>
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="d-flex gap-2">
          <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={!isDirty}>
            {t('cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={!isDirty || saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('saving')}...
              </>
            ) : (
              t('save')
            )}
          </Button>
        </div>
      </Form>

      <ConfirmDialog
        show={showConfirmDialog}
        title={t('confirm')}
        message={
          t('confirm_delete_saved_view', { name: viewToDelete?.name }) || `Da li zaista želite da obrišete sačuvani pregled "${viewToDelete?.name}"?`
        }
        onConfirm={() => {
          if (confirmAction) {
            confirmAction()
          }
        }}
        onCancel={() => setShowConfirmDialog(false)}
        variant="danger"
      />

      <PermissionsDialog
        show={showPermissionsDialog}
        onHide={() => {
          setShowPermissionsDialog(false)
          setSelectedViewForPermissions(null)
        }}
        onConfirm={async (formData: PermissionsFormData, _merge: boolean) => {
          if (!selectedViewForPermissions) return

          try {
            await savedViewService.updateSavedView(selectedViewForPermissions.id, {
              set_permissions: formData.set_permissions,
              owner: formData.owner,
            } as any)
            await loadSavedViews()
            setShowPermissionsDialog(false)
            setSelectedViewForPermissions(null)
          } catch (err) {
            setError(t('error_saving_permissions'))
          }
        }}
        object={selectedViewForPermissions || undefined}
      />
    </div>
  )
}
